import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { format } from "date-fns";
import { toast } from "sonner";
import { CheckCircle2, Clock, Loader2, LogOut, Mail, Phone, RefreshCw, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AdminShell } from "@/components/admin/AdminShell";
import { hasAdminAccess } from "@/lib/admin-access";
import { useAdminSearch, matchesQuery } from "@/components/admin/AdminSearchContext";
import { buildWhatsAppUrl } from "@/lib/phone";
import { broadcastAdminDataUpdated, useAdminDataRefresh } from "@/lib/admin-refresh";
import {
  listPaymentAttempts,
  markPaymentVerified,
  recheckRazorpayStatus,
  type AttemptRow,
} from "@/lib/admin-payment-ops.functions";

export const Route = createFileRoute("/admin/pending-payments")({
  head: () => ({
    meta: [
      { title: "Pending Payments - Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PendingPaymentsPage,
});

type Tab = "pending" | "failed" | "cleared" | "all";

const STATUS_BADGE: Record<AttemptRow["status"], { label: string; className: string }> = {
  paid: { label: "🟢 Paid", className: "bg-emerald-100 text-emerald-900 border-emerald-200" },
  failed: { label: "🔴 Failed", className: "bg-rose-100 text-rose-900 border-rose-200" },
  created: { label: "⏳ Pending", className: "bg-amber-100 text-amber-900 border-amber-200" },
};

function PendingPaymentsPage() {
  const navigate = useNavigate();
  const { query } = useAdminSearch();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [rows, setRows] = useState<AttemptRow[]>([]);
  const [tab, setTab] = useState<Tab>("pending");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [cleared, setCleared] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);

  const fetchAttempts = useServerFn(listPaymentAttempts);
  const verifyFn = useServerFn(markPaymentVerified);
  const recheckFn = useServerFn(recheckRazorpayStatus);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Purge stale rows first so the UI can never show old statuses while the
      // fresh attempt list is being fetched.
      const res = await fetchAttempts();
      setRows(res.attempts);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't load attempts");
    } finally {
      setRefreshing(false);
    }
  }, [fetchAttempts]);

  useAdminDataRefresh(load);

  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin/pending-payments" } });
        return;
      }
      if (!(await hasAdminAccess(sess.session))) {
        setAuthorized(false);
        setLoading(false);
        return;
      }
      setAuthorized(true);
      await load();
      setLoading(false);
    })();
  }, [navigate, load]);

  const counts = useMemo(
    () => ({
      pending: rows.filter((r) => r.status === "created").length,
      failed: rows.filter((r) => r.status === "failed").length,
      cleared: rows.filter((r) => r.status === "paid").length,
      all: rows.length,
    }),
    [rows],
  );

  const visible = useMemo(() => {
    return rows
      .filter((r) => {
        if (tab === "all") return true;
        if (tab === "pending") return r.status === "created";
        if (tab === "failed") return r.status === "failed";
        return r.status === "paid";
      })
      .filter((r) => matchesQuery(query, r.buyerName, r.buyerEmail, r.buyerPhone, r.orderId, r.planName));
  }, [rows, tab, query]);

  const handleVerify = async (r: AttemptRow) => {
    if (!window.confirm(`Mark ${r.buyerName}'s ₹${r.amount.toLocaleString("en-IN")} attempt as paid?`)) return;
    setBusyId(r.id);
    try {
      await verifyFn({ data: { paymentId: r.id } });
      setCleared((p) => new Set(p).add(r.id));
      toast.success("Marked as verified — moved to cleared payments");
      await load();
      broadcastAdminDataUpdated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't verify");
    } finally {
      setBusyId(null);
    }
  };

  const handleRecheck = async (r: AttemptRow) => {
    setBusyId(r.id);
    try {
      const res = await recheckFn({ data: { paymentId: r.id } });
      const msg = "message" in res && res.message ? res.message : "Status refreshed";
      if (res.ok) toast.success(msg);
      else toast.message(msg);
      await load();
      broadcastAdminDataUpdated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Recheck failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading)
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  if (!authorized)
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <h1 className="font-display text-2xl">Not authorized</h1>
      </div>
    );

  return (
    <AdminShell
      title="Pending Payments"
      description="Every raw checkout attempt — nothing merged or deduplicated."
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" disabled={refreshing} onClick={() => void load()} className="gap-2">
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              await supabase.auth.signOut();
              navigate({ to: "/auth" });
            }}
            className="gap-2"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      }
    >
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {(
          [
            { key: "pending", label: "Pending / Attempted", value: counts.pending, icon: Clock, ring: "ring-amber-500" },
            { key: "failed", label: "Failed", value: counts.failed, icon: XCircle, ring: "ring-rose-500" },
            { key: "cleared", label: "Cleared / Paid", value: counts.cleared, icon: CheckCircle2, ring: "ring-emerald-500" },
            { key: "all", label: "All attempts", value: counts.all, icon: RefreshCw, ring: "ring-primary" },
          ] as Array<{ key: Tab; label: string; value: number; icon: typeof Clock; ring: string }>
        ).map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-left rounded-2xl border border-border bg-card p-4 transition hover:border-primary/40 ${
                tab === t.key ? `ring-2 ${t.ring}` : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">{t.label}</span>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-2 font-display text-2xl font-semibold">{t.value}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-3">
        {visible.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
            No attempts in this view.
          </div>
        )}
        {visible.map((r) => {
          const badge = STATUS_BADGE[r.status];
          const busy = busyId === r.id;
          return (
            <article key={r.id} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display text-lg font-semibold">{r.buyerName}</h3>
                    <Badge variant="outline" className={badge.className}>
                      {badge.label}
                    </Badge>
                    <Badge variant="outline" className="bg-muted text-muted-foreground">
                      {r.statusReason}
                    </Badge>
                    {cleared.has(r.id) && (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">
                        cleared just now
                      </Badge>
                    )}
                    <Badge variant="secondary">{r.planName}</Badge>
                    <Badge variant="outline">₹{r.amount.toLocaleString("en-IN")}</Badge>
                    <Badge variant="outline" className={r.isManual ? "bg-amber-50 text-amber-900 border-amber-200" : "bg-sky-50 text-sky-900 border-sky-200"}>
                      {r.isManual ? "✍️ Manual" : "💳 Razorpay"}
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {r.buyerPhone && (
                      <a
                        href={buildWhatsAppUrl(r.buyerPhone, `Hi ${r.buyerName}! We noticed your WeLivdIt payment didn't complete. Can we help?`)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        <Phone className="h-3.5 w-3.5" /> {r.buyerPhone}
                      </a>
                    )}
                    {r.buyerEmail && (
                      <a href={`mailto:${r.buyerEmail}`} className="inline-flex items-center gap-1 hover:text-primary">
                        <Mail className="h-3.5 w-3.5" /> {r.buyerEmail}
                      </a>
                    )}
                    <span>Attempted {format(new Date(r.createdAt), "PP p")}</span>
                  </div>
                  <div className="mt-1 break-all font-mono text-xs text-muted-foreground">
                    {r.orderId && <>order: {r.orderId} </>}
                    {r.paymentId && <> · pay: {r.paymentId}</>}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {r.status !== "paid" && (
                    <Button size="sm" disabled={busy} onClick={() => void handleVerify(r)} className="gap-1.5">
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      Mark as Verified
                    </Button>
                  )}
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => void handleRecheck(r)} className="gap-1.5">
                    <RefreshCw className="h-3.5 w-3.5" /> Recheck Razorpay
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </AdminShell>
  );
}
